import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  EventEmitter,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NavigationBehaviorOptions, Router } from '@angular/router';
import { ModalService } from '@purespectrum1/ui/modal';

// Constants
import { notifyMessage } from '../../../shared/constants/notify-message';
import { SURVEY_STATUS } from '../../../shared/constants/survey-status';
import { Constants } from '../../../shared/constants/create-survey.constants';
import {
  STEPPER_BUTTON_STATUS,
  SURVEY_PAGES,
} from '../../constants/stepper-button-status';

import {
  SurveyNoTargetingResponse,
  BuyerSurveyResponse,
  GetSupplierBlendInterface,
  SupplierResponseInterface,
} from '../../../shared/interfaces/survey.interface';
import {
  SelectedTemplate,
  TemplateRequest,
  TemplateResponse,
} from '../template-modal/template-modal.interface';
import {
  CPIData,
  FeotInfo,
  SurveyPayload,
  UpdateSupplierPayloadInterface,
} from '../../../shared/interfaces/survey-meta-data.interface';

// Services
import { SurveyPayloadService } from '../../survey-payload.service';
import { EventEmitterService } from '../../../shared/services/event-emitter/event-emitter.service';
import { CreateSurveyService } from '../../create-survey.service';
import { BuyerApiService } from '../../../shared/services/buyer-api/survey.service';
import { EditSupplierModalService } from '../edit-supplier-modal.service';
import { MultiCountryDraftService } from '../../services/multi-country-draft/multi-country-draft.service';
import { SurveyMetaDataService } from '../../../shared/services/survey-meta-data/survey-meta-data.service';

// Components
import { SupplierCtUpdateWarningModalComponent } from '../../shared/ui/supplier-ct-update-warning-modal/supplier-ct-update-warning-modal.component';
import { EditSurveyConfirmationModalComponent } from '../../shared/ui/edit-survey-confirmation-modal/edit-survey-confirmation-modal.component';
import { ConfirmationModalComponent } from '../../../shared/ui/confirmation-modal/confirmation-modal.component';
import { TRAFFIC_CHANNEL } from '../../constants/traffic-channels-constants';
import { defer, forkJoin, iif, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, skip, switchMap } from 'rxjs/operators';
import { BuyerConfig } from '../../../shared/interfaces/buyer-config.interface';
import { CpiService } from '../../services/cpi/cpi.service';
import { CPI } from '../../../shared/interfaces/cpi.interface';
import { TemplateService } from '../../../shared/services/template/template.service';
import { TemplateModalComponent } from '../template-modal/template-modal.component';
import {
  IndividualConfig,
  ToasterService,
} from '@purespectrum1/ui/toaster-service';
import { AuthService } from '../../../shared/services/auth/auth.service';
import { MarketplaceService } from '../../marketplace.service';
import { MissingLiveUrlModalComponent } from '../../../survey-launch/missing-live-url-modal/missing-live-url-modal.component';
import { LaunchModalComponent } from '../../../survey-launch/launch-modal/launch-modal.component';
import { QuotaPayload } from '../../../shared/interfaces/quota.interface';
import { QuotaLevelTrackingModalComponent } from '../../../survey-launch/quota-mapper/quota-level-tracking-modal/quota-level-tracking-modal.component';
import { QUOTA_LEVEL_TRACKING } from '../../../shared/constants/quota-level-tracking';
import { LayoutConstants } from '../layout-constants';
import { ChurnZeroConsts, ChurnZeroService } from '@purespectrum1/ui/tracking';
import { GlobalConstants } from '../../../shared/constants/global.constants';
import { SURVEY_LAUNCH_CONSTANTS } from '../../../shared/constants/survey-launch.constants';
import { Constants as BuyerConstant } from '../../../shared/constants/buyer-constant';
import { deepCopy } from '../../utils/deep-copy';
import { SurveyUpdateError } from '../../../shared/services/buyer-api/domain/survey-update-error';
import {
  CountrySurveyData,
  McSpvSurveyRequest,
  MultiCountrySurvey,
} from '../../shared/ui/multicountry/interface/multi-country.interface';
import { MultiCountryService } from '../../shared/ui/multicountry/services/multi-country.service';
import {
  McSvpSetterData,
  McSvpSurveyPayloadComposer,
} from '../../shared/ui/multicountry/domain/mc-svp-survey-payload-composer';
import {
  McTraditionalSettings,
  McTraditionalSurveySettingsComposer,
} from '../../shared/ui/multicountry/domain/mc-traditional-survey-settings-composer';
import { InactiveQualificationsMessageComposer } from '../../../shared/utils/inactive-qualifications-message-composer';
import { UniqueLinksValidator } from '../../domain/unique-links-validator';
import { AidaSurveyPayloadTransformer } from '../../../survey-launch/ai-delivery-assistant/domain/survey-payload-transformer-for-aida';
import { AidaSurveyErrorDetails } from '../../../shared/interfaces/aida.interface';
import { AidaPayloadService } from '../../../shared/services/aida/aida-payload/aida-payload.service';
import { SurveyStatus } from '../../../shared/utils/survey-status';
import { PurePriceOverride } from '../../services/domain/pure-price-override';
import { Common } from '../../../shared/domain/common';
import { DEFAULT } from '../../../shared/constants/defaults';

@Component({
  selector: 'ps-ui-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.css'],
})
export class FooterComponent implements OnInit, OnChanges, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  private _supplierBlendData?: GetSupplierBlendInterface[];
  public stepperButtonStatusObj = STEPPER_BUTTON_STATUS;
  public surveyStatusObj = SURVEY_STATUS;
  public surveyId?: number;
  public surveyStatus: number = SURVEY_STATUS.DRAFT;
  public liveUrlModalConfirmed: boolean = false;
  public duplicatedBuyerQuotaIds: string[] = [];
  public charCountExceedQuotas: string[] = [];
  public hasMcPreDraftData: boolean = false;
  public cpi: CPI[] = [];
  public surveyData?: {
    surveyId: number;
    mcUniqueId?: string;
    activeTrafficChannel: boolean;
  };
  public multiCountrySurveyData: MultiCountrySurvey[] = [];
  public TrafficChannel = TRAFFIC_CHANNEL;
  public enableFilteringByDate: boolean = false;
  manualOverride: boolean = false;
  surveyPauseThreshold: number = 0;
  surveyPriceOverride: number = 0;
  supplierPayload: UpdateSupplierPayloadInterface =
    {} as UpdateSupplierPayloadInterface;
  pricingMultiplier: number =
    Constants.SURVEY_PAYLOAD_DEFAULTS.PRICE_MULTIPLIER;
  public disableLaunch: boolean = true;
  public feotFields: FeotInfo = {
    feotQuantity: BuyerConstant.FEOT_INFO.DEFAULT_QUANTITY,
    feotRemainingTime: BuyerConstant.FEOT_INFO.DEFAULT_REMAINING_TIME,
  };
  private _isBpmRequired: boolean = false;
  private _isOperator: boolean = false;

  @Input() buttonStatus: number = STEPPER_BUTTON_STATUS.DEFINE_AUDIENCE;
  @Input() surveyPayload!: SurveyPayload;
  @Input() currentPage!: string;
  @Input() manualProcessing: boolean = false;
  @Input() cpiAndCompensationConfirmed: boolean = true;
  @Input() templateResponse?: TemplateResponse;
  @Input() cpiOverrideConfirmationState = true;
  @Input() waspBuyerCpiOverrideState = false;
  @Input() isInsight: boolean = false;
  @Input() billingRuleExample: string | undefined;
  @Input() selectedMcSpvChild?: CountrySurveyData;

  @Output() updatedTemplate = new EventEmitter();
  public isServiceBuyerLoggedIn: boolean = false;
  private _buyerConfigSettings!: BuyerConfig;
  private _isPurePriceRatesEnabled: boolean = false;
  public isLimitedDraftBuyer: boolean = false;
  private _suppliers?: SupplierResponseInterface[];
  private _mcSvpPayloadComposer = new McSvpSurveyPayloadComposer(
    this._auth.userType
  );
  private _mcTraditionalSettingsComposer =
    new McTraditionalSurveySettingsComposer();
  private _previouslySelectedMcSpvChildId?: number;
  private _aidaSurveyErrorDetails: AidaSurveyErrorDetails; // only case of supplier edit page

  constructor(
    private _router: Router,
    private _modal: ModalService,
    private _toastr: ToasterService,
    private _surveyMetaDataService: SurveyMetaDataService,
    private _buyerApiService: BuyerApiService,
    private _createSurveyService: CreateSurveyService,
    private _cpiService: CpiService,
    private _editSupplierModalService: EditSupplierModalService,
    private _multiCountryDraftService: MultiCountryDraftService,
    private _surveyPayloadService: SurveyPayloadService,
    private _eventEmitterService: EventEmitterService,
    private _churnZeroService: ChurnZeroService,
    private _templateService: TemplateService,
    private _auth: AuthService,
    private _marketplaceService: MarketplaceService,
    private _multiCountryService: MultiCountryService,
    private _aidaPayloadService: AidaPayloadService
  ) {}

  ngOnInit() {
    this._mcSvpPayloadComposer = new McSvpSurveyPayloadComposer(
      this._auth.userType,
      this._auth.isLoggedInAsServiceBuyer()
    );
    this._isBpmRequired = !!this._auth.buyerConfig?.enablePMRequired;
    this._isOperator = this._auth.userType === 'operator';
    this.enableFilteringByDate = !!this._auth.buyerConfig?.includeToo;
    this.isLimitedDraftBuyer =
      this._auth.user?.buyerAcssLvls === 'limitedDraft';
    this._subscriptions.add(
      this._surveyMetaDataService
        .getSurveyHeaderData()
        .subscribe((surveyResponse) => {
          if (surveyResponse) {
            this.surveyId = surveyResponse.surveyId;
            this.surveyStatus =
              surveyResponse.surveyStatus || SURVEY_STATUS.DRAFT;
            if (this.surveyPayload) {
              this.surveyPayload.field_time = surveyResponse?.surveyFieldTime;
            }
          }
        })
    );

    this._subscriptions.add(
      this._surveyMetaDataService
        .getQuotaMapperValidateData()
        .subscribe((mapperValidate) => {
          this.charCountExceedQuotas = mapperValidate.charCountExceedQuotas;
          this.duplicatedBuyerQuotaIds = mapperValidate.duplicatedBuyerQuotaIds;
        })
    );

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMultiCountryPreDraftData()
        .subscribe((data) => {
          this.hasMcPreDraftData = !!data.length;
        })
    );
    this._subscriptions.add(
      this._surveyMetaDataService
        .getCPIDropdownData()
        .subscribe((data: CPI[]) => {
          this.cpi = data;
        })
    );
    this._subscriptions.add(
      this._surveyMetaDataService
        .getCPIData()
        .pipe(skip(1))
        .subscribe((data: CPIData) => {
          this.disableLaunch = this.manualProcessing ? false : !!data.loading;
          if (!data.suppliers || !data.buyerData || !data.feasibility) {
            return;
          }
          const { suppliers, feasibility, buyerData } = data;
          this._suppliers = suppliers;
          this.surveyPriceOverride =
            data.flexibilityPrice?.survey_price_override || 0;
          this.surveyPayload.price_override = this.surveyPriceOverride;

          this.manualOverride = data.flexibilityPrice?.manual_override || false;
          this.surveyPauseThreshold =
            data.flexibilityPrice?.survey_pause_threshold || 0;
          const isActiveSurvey = Number(buyerData.surveyId) === this.surveyId;
          this.cpi = this._cpiService.mapCpi(
            suppliers,
            buyerData,
            feasibility,
            isActiveSurvey
          );
          this.pricingMultiplier = feasibility.hasOwnProperty(
            'pricing_multplier'
          )
            ? feasibility.pricing_multplier
            : Constants.SURVEY_PAYLOAD_DEFAULTS.PRICE_MULTIPLIER;
        })
    );

    this._subscriptions.add(
      this._surveyMetaDataService
        .getSupplierListPayload()
        .pipe(filter((value) => value && Object.keys(value).length > 0))
        .subscribe((supplier) => {
          this.supplierPayload = deepCopy(supplier);
        })
    );

    this._subscriptions.add(
      this._surveyMetaDataService
        .getSuppliersSelected()
        .subscribe((selectedSupplier) => {
          if (selectedSupplier.suppliers) {
            this.supplierPayload.suppliers = selectedSupplier.suppliers;
          }

          if (selectedSupplier.blend_id || selectedSupplier.blend_id === '') {
            this.supplierPayload.blend_id = selectedSupplier.blend_id;
          }
        })
    );

    this._subscriptions.add(
      this._surveyMetaDataService
        .getSupplierOverrideValue()
        .subscribe((manualScpiOverrideEnabled) => {
          this.supplierPayload.manualScpiOverrideEnabled =
            manualScpiOverrideEnabled;
        })
    );
    this._getSurveyData();
    this._getMultiCountryData();

    this._subscriptions.add(
      this._surveyMetaDataService.listenSaveSurvey().subscribe(() => {
        // this is subscription of
        this.updateSurvey('saveAndProcess');
      })
    );
    this._eventEmitterService.sendFeasibilityCpiEmitter.subscribe((data) => {
      this.surveyPayload.feasibility_cpi = data.totalCpi;
    });

    const authServiceSubscription =
      this._auth.loggedInAsServiceBuyer$.subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this.isServiceBuyerLoggedIn = true;
        }
      });
    this._subscriptions.add(authServiceSubscription);
    this._buyerConfigSettings = this._auth.buyerConfig!;

    const templateSuppliersSubscription = this._templateService.supplierInfo
      .pipe(filter(() => !this.supplierPayload.suppliers))
      .subscribe((suppliers) => {
        this.supplierPayload.suppliers = suppliers;
      });

    this._subscriptions.add(templateSuppliersSubscription);
    this._subscriptions.add(
      this._surveyMetaDataService.updateSurveyPayload.subscribe((payload) => {
        this._updateSurveyPayload(payload);
        this._updateSpvChildSurveyPaylod(payload);
      })
    );

    this._subscriptions.add(
      this._surveyMetaDataService
        .getPurePriceOverride()
        .subscribe((purePriceOverride) => {
          this.surveyPayload.default_pure_price_override =
            purePriceOverride.isActive();
          this.supplierPayload.defaultPurePriceOverride =
            purePriceOverride.isActive();
        })
    );

    this._subscriptions.add(
      this._surveyMetaDataService
        .getSupplierSpecificInfo()
        .subscribe((data) => {
          this._supplierBlendData = data.supplierBlendData;
        })
    );

    this._subscriptions.add(
      this._surveyMetaDataService
        .getPurePriceRateCardEnable()
        .subscribe((purePriceRateCardEnable) => {
          this._isPurePriceRatesEnabled = purePriceRateCardEnable;
        })
    );
    this._subscriptions.add(
      this._marketplaceService
        .onCreateSurveyComplete()
        .subscribe((response) => {
          if (response.blend_id) {
            this.supplierPayload.blend_id = response.blend_id;
          } else {
            this.supplierPayload.blend_id = '';
          }
        })
    );
    this._subscriptions.add(
      this._eventEmitterService.FeotFieldCalculationEmitter.subscribe(
        (data) => {
          this.feotFields = data;
        }
      )
    );

    this._subscriptions.add(
      this._multiCountryDraftService
        .getSpvSurveyResponseData()
        .pipe(filter((data) => !!data.length))
        .subscribe((data) => {
          this._mcSvpPayloadComposer.initial(data);
          this._multiCountryDraftService.sendMcSpvSurveyRequest(
            this._mcSvpPayloadComposer.payload()!
          );
          this._multiCountryDraftService.sendMcSvpUpdatedSurveysResponse(
            this._mcSvpPayloadComposer.updated()
          );
          this._forcePatchUniqueLinksForMCSvpAfterDataLoading();
        })
    );

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcSpvUpdateInCacheCommand()
        .pipe(
          skip(1),
          filter(() => !!this._mcSvpPayloadComposer.payload()) // Allowing to patch further data when intial data is set
        )
        .subscribe((data) => {
          this._mcSvpPayloadComposer.set(data);
          this._multiCountryDraftService.sendMcSpvSurveyRequest(
            this._mcSvpPayloadComposer.payload()!
          );
          this._multiCountryDraftService.sendMcSvpUpdatedSurveysResponse(
            this._mcSvpPayloadComposer.updated()
          );
        })
    );

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcSpvRemoveFromCache()
        .pipe(
          filter(() => !!this._mcSvpPayloadComposer.payload()) // Allowing to delete further data only when intial data is set
        )
        .subscribe((data) => {
          this._mcSvpPayloadComposer.remove(data);
          this._multiCountryDraftService.sendMcSpvSurveyRequest(
            this._mcSvpPayloadComposer.payload()!
          );
          this._multiCountryDraftService.sendMcSvpUpdatedSurveysResponse(
            this._mcSvpPayloadComposer.updated()
          );
        })
    );

    this._handleMcTraditionalSettingsUpdate();
    this._handleAidaSupplierErrorDetails();
  }

  get isTargetMarginEnabledForOperatorBuyer(): boolean {
    return (
      this.isServiceBuyerLoggedIn &&
      this._buyerConfigSettings.enableTargetMargin
    );
  }

  private _updateSurveyPayload(payload: SurveyPayload) {
    if (payload) {
      this.surveyPayload = Object.assign(this.surveyPayload, payload);
    }
  }

  private _updateSpvChildSurveyPaylod(payload: SurveyPayload) {
    if (!this._isMcSvpSurvey()) {
      return;
    }

    const update = {
      survey_localization: this.surveyPayload.survey_localization,
      feasibility_exclusions: payload.feasibility_exclusions,
    };
    const cache = {
      for: 'each' as const,
      data: [update],
    };
    this._multiCountryDraftService.sendMcSpvUpdateInCacheCommand(cache);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.surveyPayload?.ps_survey_id) {
      this.surveyId = this.surveyPayload.ps_survey_id;
    }

    if (this.surveyPayload?.schedule_launch?.enable) {
      this.liveUrlModalConfirmed = false;
    }
    if (
      changes.selectedMcSpvChild?.currentValue &&
      changes.selectedMcSpvChild.currentValue.ps_survey_id !==
        changes.selectedMcSpvChild.previousValue?.ps_survey_id
    ) {
      this._onMcSpvSelection();
    }
  }

  private _forcePatchUniqueLinksForMCSvpAfterDataLoading() {
    if (!this._isMcSvpSurvey()) {
      return;
    }

    if (!this.surveyPayload?.unique_links) {
      return;
    }
    const { unique_links } = this.surveyPayload;
    const update: McSvpSetterData = {
      for: 'all',
      data: {
        unique_links,
      },
    };
    if (this.surveyPayload?.is_same_file_entry_links) {
      this._multiCountryDraftService.sendMcSpvUpdateInCacheCommand(update);
    }
  }

  private _handleMcTraditionalSettingsUpdate(): void {
    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcTraditionalUpdateInCache()
        .pipe(filter((update) => update && !!update.length))
        .subscribe((update) => {
          this._mcTraditionalSettingsComposer.set(update);
          this._multiCountryDraftService.sendMcTraditionalSettings(
            this._mcTraditionalSettingsComposer.get()
          );
        })
    );

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcTraditionalSettingsForReset()
        .subscribe(() => {
          this._mcTraditionalSettingsComposer.reset();
          this._multiCountryDraftService.sendMcTraditionalSettings(
            this._mcTraditionalSettingsComposer.get()
          );
        })
    );

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMcTraditionalSettingsForRemoval()
        .subscribe((surveyId) => {
          this._mcTraditionalSettingsComposer.remove(surveyId);
          this._multiCountryDraftService.sendMcTraditionalSettings(
            this._mcTraditionalSettingsComposer.get()
          );
        })
    );
  }

  save(action: string | null = null) {
    if (this.hasMcPreDraftData) {
      this._toastr.error(
        notifyMessage.errorMessage.MULTICOUNTRY_DRAFT.FOOTER_SAVE
      );
    } else {
      this.updateSurvey(action);
    }
  }

  goToEditAudience() {
    if (this.currentPage !== SURVEY_PAGES.AUDIENCE) {
      this.updateSurvey('goToEditAudience');
    }
  }

  goToSurveyLaunch() {
    // prevent navigating to the survey launch while endpoints or loading survey data
    if (this.disableLaunch || !this.waspBuyerCpiOverrideState) {
      return;
    }

    if (this.hasMcPreDraftData) {
      this._toastr.error(
        notifyMessage.errorMessage.MULTICOUNTRY_DRAFT.FOOTER_FINALIZE_AND_LAUNCH
      );
    } else {
      this.updateSurvey('goToSurveyLaunch');
    }
  }

  goToMapQuotas() {
    if (this._missingRequiredBpm()) {
      return this._toastr.error(
        this._modifyStringForBuyerRole(
          notifyMessage.errorMessage.SURVEY_LAUNCH.BPM_REQUIRED_ON_LAUNCH
        )
      );
    }

    if (this._isLiveUrlExist()) {
      this.updateSurvey('goToMapQuotas');
    } else {
      this._toastr.error(
        notifyMessage.errorMessage.SURVEY_LAUNCH.EMPTY_LIVE_URL
      );
      return;
    }
  }

  launchSurvey() {
    if (this.currentPage === SURVEY_PAGES.AUDIENCE) {
      this.goToSurveyLaunch();
      return;
    }

    if (this.currentPage === SURVEY_PAGES.MAP_QUOTA) {
      const quotas = this.surveyPayload.quotas;
      if (quotas?.length) {
        this._quotaMapperValidator(quotas);
      }
    }

    if (this.currentPage === SURVEY_PAGES.LAUNCH) {
      if (
        this.surveyPayload?.schedule_launch?.enable &&
        !this.liveUrlModalConfirmed
      ) {
        this._openLiveUrlConfirmModal();
        return;
      }

      if (this._isValidPayload()) {
        if (this.surveyPayload?.schedule_launch?.enable) {
          this._launchscheduleLaunchSurvey();
        } else {
          this.updateSurvey('OpenModal');
        }
      }
    }
  }

  private _isMcSvpSurvey(): boolean {
    return !!this._mcSvpPayloadComposer.payload();
  }

  private _surveyPayload() {
    if (this._isMcSvpSurvey()) {
      return this._mcSvpPayloadComposer.payload();
    }

    return this._mapPayload();
  }

  private _mcTraditionalSettings() {
    return this._mcTraditionalSettingsComposer.get();
  }

  /**
   * For a MC-QB survey, this.surveyId reflects the currently selected child's ID, but
   * in routing scenarios, or update survey scenarios, we need to use parent ID.
   */
  public getSurveyId(): number {
    if (this._isMcSvpSurvey()) {
      return this._mcSvpPayloadComposer.surveyId();
    }

    return this.surveyId!;
  }

  private _prepareNavigationExtras(): NavigationBehaviorOptions {
    if (this._isMcSvpSurvey()) {
      return {
        state: {
          svp_child_id: this.surveyId,
        },
      };
    }

    return {};
  }

  private _validatePayload(
    shouldValidateMargin: boolean = false,
    action: string | null = null
  ) {
    this._validateSurveyTitle();
    this._validateCategory();
    this._validateCompletes();
    this._validateLoi();
    this._validateIncidence();
    this._validateFildTime();
    this._validatePmEmails();
    this._validatePoNumber();
    this._validateMargin(shouldValidateMargin);
    this._validateFeotIntervalTime();
    this._validateAidaSurveyErrors();
    this._validateUniqueLinks(action);
  }

  private _validateUniqueLinks(action: string | null) {
    if (this.surveyPayload?.unique_links?.enable) {
      const validator = UniqueLinksValidator.from(this.surveyPayload);
      if (
        !validator.validate() &&
        action &&
        ['LiveEdit', 'goToSurveyLaunch'].includes(action)
      ) {
        const errors: { [key: string]: string } = {
          LiveEdit: notifyMessage.errorMessage.UNIQUE_LINKS_REQUIRED.LIVE,
          goToSurveyLaunch:
            notifyMessage.errorMessage.UNIQUE_LINKS_REQUIRED.DRAFT,
        };

        throw new Error(
          errors[action].replace(
            '<QUANTITY>',
            validator.requiredUniqueLinks().toString()
          )
        );
      }
    }
  }

  private _validateAidaSurveyErrors() {
    if (
      this.surveyPayload?.aida?.enabled &&
      this._aidaSurveyErrorDetails &&
      this._aidaSurveyErrorDetails?.showError
    ) {
      throw new Error(this._aidaSurveyErrorDetails.message);
    }
  }

  private _validatePoNumber() {
    if (
      this.surveyPayload.billing_id &&
      this.surveyPayload.billing_id.length >
        GlobalConstants.MAX_ALLOWED_CHARS_FOR_PO
    ) {
      throw new Error(
        notifyMessage.errorMessage.PO_CHARACTER_LIMIT_EXCEED.replace(
          '<MAX_CHAR>',
          GlobalConstants.MAX_ALLOWED_CHARS_FOR_PO.toString()
        )
      );
    }
  }

  private _validateMargin(isSurveyCreation: boolean) {
    // validate only on survey creation
    if (!isSurveyCreation) {
      return;
    }
    const min = -99;
    const max = 99;
    if (
      this.surveyPayload.margin_override?.margin &&
      (this.surveyPayload.margin_override?.margin < min ||
        this.surveyPayload.margin_override?.margin > max)
    ) {
      throw new Error(
        `Margin should be between ${min}% and ${max}% on survey creation.`
      );
    }
  }

  private _validatePmEmails() {
    const regex =
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const isProjectManagerEmailsInvalid = this.surveyPayload.pm_emails?.some(
      (email) => {
        return email === '' || !regex.test(email.trim());
      }
    );
    if (isProjectManagerEmailsInvalid) {
      throw Error(notifyMessage.errorMessage.INVALID_EMAIL);
    }
  }

  private _validateSurveyTitle() {
    if (!this.surveyPayload.survey_title) {
      throw Error(Constants.CREATE_SURVEY_ERROR_MESSAGE.SURVEY_TITLE);
    }
  }

  private _isBlank(str?: string) {
    return !str || /^\s*$/.test(str);
  }

  private _validateLoi() {
    if (
      !this.surveyPayload.expected_loi ||
      this.surveyPayload.expected_loi > 60 ||
      this.surveyPayload.expected_loi == 0
    ) {
      throw Error(Constants.CREATE_SURVEY_ERROR_MESSAGE.LENGTH_OF_SURVEY_VALUE);
    }

    if (!this.surveyPayload.expected_loi) {
      throw Error(Constants.CREATE_SURVEY_ERROR_MESSAGE.LENGTH_OF_SURVEY);
    }
  }

  private _validateCategory() {
    if (!this.surveyPayload.survey_category_code) {
      throw Error(Constants.CREATE_SURVEY_ERROR_MESSAGE.SELECT_CATEGORY);
    }
  }

  private _validateCompletes() {
    if (
      !this.surveyPayload.completes_required &&
      this.surveyPayload.survey_category_code !==
        Constants.SURVEY_CATEGORY_CODE.AUGMENT
    ) {
      throw Error(Constants.CREATE_SURVEY_ERROR_MESSAGE.COMPLETES);
    }
  }

  private _validateFildTime() {
    if (!this.surveyPayload.field_time) {
      throw Error(Constants.CREATE_SURVEY_ERROR_MESSAGE.FIELD_TIME);
    }
  }

  private _validateIncidence() {
    if (!this.surveyPayload.expected_ir) {
      throw Error(Constants.CREATE_SURVEY_ERROR_MESSAGE.INCIDENCE);
    }
  }

  private _openLiveUrlConfirmModal() {
    const modalRef = this._modal.open(MissingLiveUrlModalComponent, {
      data: { surveyId: this.surveyId },
    });

    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'ok') {
        this.liveUrlModalConfirmed = true;
      }
    });
  }

  private _openSurveyLaunchModal() {
    const modalRef = this._modal.open(LaunchModalComponent, {
      data: { surveyId: this.surveyId },
    });
    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'Launch') {
        if (!this.surveyPayload.crmId) {
          this.surveyPayload.crmId = this.surveyPayload.crm_id;
        }
        if (!this.surveyPayload.asanaId) {
          this.surveyPayload.asanaId = this.surveyPayload.asana_id;
        }
        if (
          this.surveyPayload.unique_links?.enable &&
          (!this.surveyPayload.unique_links.items ||
            !this.surveyPayload.unique_links.items.length)
        ) {
          return this._toastr.error(
            notifyMessage.errorMessage.SURVEY_LAUNCH
              .UNIQUE_BUYER_LINKS_NOT_UPLOAD
          );
        }
        if (
          !this.surveyPayload.unique_links?.enable &&
          !this._isLiveUrlExist()
        ) {
          return this._toastr.error(
            notifyMessage.errorMessage.SURVEY_LAUNCH.EMPTY_LIVE_URL
          );
        }
        if (!this.cpiAndCompensationConfirmed) {
          return this._toastr.error(
            notifyMessage.errorMessage.SURVEY_LAUNCH.CPI_COMPENSATION_CONFIRMED
          );
        }
        if (
          ((this.isServiceBuyerLoggedIn &&
            this.surveyPayload.is_enable_margin_override) ||
            this._buyerConfigSettings.enableTargetMarginLaunchAll) &&
          !this.surveyPayload.price_override
        ) {
          return this._toastr.error(
            notifyMessage.errorMessage.SURVEY_LAUNCH.PRICE_OVERRIDE_NOT_FOUND
          );
        }
        if (
          this.isServiceBuyerLoggedIn &&
          this.surveyPayload.is_enable_margin_override &&
          this._isBlank(this.surveyPayload.crmId)
        ) {
          return this._toastr.error(
            Constants.CREATE_SURVEY_ERROR_MESSAGE.REQUIRED_CRM_ID
          );
        }
        if (this._missingRequiredBpm()) {
          return this._toastr.error(
            this._modifyStringForBuyerRole(
              notifyMessage.errorMessage.SURVEY_LAUNCH.BPM_REQUIRED_ON_LAUNCH
            )
          );
        }
        if (this._isMcSvpSurvey() && !this._validateSurveyLinksAndUrl()) {
          return;
        }

        this.updateSurvey('Launch');
      }
    });
  }

  private async _saveSupplierAllocationData() {
    // We need to use price offer when using WASP
    // const price = this._auth.isLoggedInAsServiceBuyer()
    //   ? this.surveyPayload.offer_price
    //   : this.surveyPriceOverride;
    const completesOrClicksToAllocate = this.surveyPayload.click_balancing
      ? this.surveyPayload.estimated_clicks
      : this.surveyPayload.completes_required;
    this._surveyMetaDataService.setShowPurepriceFlyout(
      this.manualOverride,
      !!this.supplierPayload.manualScpiOverrideEnabled
    );
    const isAugmentSurvey =
      Constants.SURVEY_CATEGORY_CODE.AUGMENT ===
      this.surveyPayload.survey_category_code;
    const suppliers = await this._cpiService.setSuppliers(
      this.cpi,
      this.surveyPriceOverride,
      this.manualOverride,
      this.surveyPauseThreshold,
      completesOrClicksToAllocate,
      this.isServiceBuyerLoggedIn,
      this.supplierPayload.manualScpiOverrideEnabled,
      this._supplierBlendData,
      this.supplierPayload.blend_id,
      this._isPurePriceRatesEnabled,
      this._suppliers,
      isAugmentSurvey,
      this.surveyPayload.default_pure_price_override
    );
    this._surveyMetaDataService.sendSuppliersSelected({
      suppliers: suppliers,
      blend_id: this.supplierPayload.blend_id,
    });
  }

  public updateSurvey(action: null | string = null) {
    try {
      // should validate margin only on survey creation
      const shouldValidateMargin = action === 'goToSurveyLaunch';
      if (this.currentPage !== SURVEY_PAGES.LAUNCH) {
        this.feotFields.feotRemainingTime = this._getFeotRemainingTime();
      }
      this._validatePayload(shouldValidateMargin, action);
    } catch (error: any) {
      this._toastr.error(error);
      return;
    }
    if (
      !this._surveyPayloadService.getProcessingPermissionToSave(
        this.manualProcessing,
        false, // isAutoSave: assume when is called from footer is never an autosave call
        true, // isFormValid: passed _validatePayload so the form is valid
        !!this.surveyId
      )
    ) {
      return;
    }

    // Ensure we always emit the draft is saved event
    if (!this.surveyId) {
      this._eventEmitterService.draftIsSavedEvent();
      return;
    }

    const payload = this._surveyPayload();
    const surveyId = this.getSurveyId();
    this._multiCountryDraftService.sendMcTraditionalUpdateCommand();

    const updateStream$ = iif(
      () => this._isMcSvpSurvey(),
      this._multiCountryService.updateSurvey(payload, surveyId),
      this._buyerApiService.updateSurvey(payload, surveyId)
    );
    updateStream$.subscribe(
      async (surveyUpdateResponse) => {
        this._churnZeroService.sendEvent(
          ChurnZeroConsts.events.draftSurveyUpdated,
          ChurnZeroConsts.eventDescriptions.draftSurveyUpdated.replace(
            ':id',
            this.surveyId + ''
          )
        );
        this._patchMcTraditionalSettings();
        if (
          this.currentPage === SURVEY_PAGES.AUDIENCE &&
          action !== 'LiveEdit'
        ) {
          try {
            await this._saveSupplierAllocationData();
            this._actionAfterUpdate(action, surveyUpdateResponse);
            this._toastr.success(notifyMessage.successMessage.SURVEY_ADDED);
          } catch (e: any) {
            this._toastr.error(e.message);
          }
        } else {
          this._actionAfterUpdate(action, surveyUpdateResponse);
        }
        this._eventEmitterService.draftIsSavedEvent();
        this._multiCountryDraftService.sendMcTraditionalSurveyRequest({});
        const inactiveQualificationMessage =
          new InactiveQualificationsMessageComposer(
            surveyUpdateResponse.inactive_qualifications,
            'update-survey'
          ).compose();
        if (inactiveQualificationMessage) {
          this._toastr.warning(inactiveQualificationMessage, '', {
            enableHtml: true,
          });
        }
      },
      (err) => {
        if (action === 'goToEditAudience') {
          this._actionAfterUpdate(action);
          return;
        }
        this._toastr.error(err.error.ps_api_response_message, '', {
          enableHtml: true,
        });
        this._eventEmitterService.draftIsSavedEvent();
      }
    );
  }

  private _verifyNoQuotasChurnZeroEvent() {
    if (!this.surveyPayload.qbp?.quota_ids?.length) {
      this._churnZeroService.sendEvent(
        ChurnZeroConsts.events.surveyLaunchedWithNoQuotas,
        ChurnZeroConsts.eventDescriptions.surveyLaunchedWithNoQuotas.replace(
          ':id',
          this.surveyId + ''
        )
      );
    }
  }

  private _patchMcTraditionalSettings() {
    if (!this.surveyPayload.mc_unique_id) {
      return;
    }

    if (this._isMcSvpSurvey()) {
      return;
    }

    forkJoin(
      this._mcTraditionalSettings().map((val) => this._patchSurvey(val))
    ).subscribe(
      () => {
        this._resetMcTraditionalSettings();
      },
      (error) => {
        this._toastr.error(error.message);
      }
    );
  }

  private _patchSurvey(item: McTraditionalSettings) {
    return this._buyerApiService
      .patchSurvey(item.survey_id, item.data)
      .pipe(
        catchError((error) =>
          throwError(new SurveyUpdateError(error, this._auth.buyerConfig!))
        )
      );
  }

  private _resetMcTraditionalSettings(): void {
    this._multiCountryDraftService.sendMcTraditionalSettingsForReset();
  }

  // eslint-disable-next-line complexity
  private _actionAfterUpdate(
    action: null | string = null,
    surveyResponse?: SurveyNoTargetingResponse
  ) {
    this._surveyMetaDataService.setIsFormDirty(false);

    switch (action) {
      case 'Launch':
        /* Set status to LIVE if schedule_launch is not enable */
        if (
          this.getSurveyId() &&
          !this.surveyPayload?.schedule_launch?.enable
        ) {
          this._buyerApiService
            .updateSurveyStatus(this.getSurveyId(), {
              ps_survey_status: SURVEY_STATUS.LIVE,
            })
            .pipe(
              catchError((error) =>
                throwError(
                  new SurveyUpdateError(
                    error,
                    this._auth.buyerConfig!,
                    this.billingRuleExample
                  )
                )
              )
            )
            .subscribe(
              () => {
                this._verifyNoQuotasChurnZeroEvent();
                this._churnZeroService.sendEvent(
                  ChurnZeroConsts.events.lauchSurveyCreated,
                  ChurnZeroConsts.eventDescriptions.lauchSurveyCreated.replace(
                    ':id',
                    this.getSurveyId() + ''
                  )
                );
                this._redirectToNextSurvey();
              },
              (error) =>
                this._toastr.error(error.message, '', {
                  enableHtml: true,
                } as IndividualConfig)
            );
        } else {
          this.goToDashboard();
        }
        break;
      case 'save':
        this._updateHeaderInfo(surveyResponse);
        break;
      case 'saveAndClone':
        if (this.surveyId) {
          this._cloneSurvey(this.surveyId);
        }
        break;
      case 'saveAndCreateNew':
        this._router.navigateByUrl('/create-surveys').then(() => {
          window.location.reload();
        });
        break;
      case 'saveAndProcess':
        this._updateHeaderInfo(surveyResponse);
        break;
      case 'goToEditAudience':
        this._router.navigateByUrl(
          `/surveys/${this.getSurveyId()}/edit`,
          this._prepareNavigationExtras()
        );
        this.buttonStatus = STEPPER_BUTTON_STATUS.DEFINE_AUDIENCE;
        break;
      case 'goToSurveyLaunch':
        this._router.navigateByUrl(
          `/launch-survey/${this.getSurveyId()}`,
          this._prepareNavigationExtras()
        );
        this.buttonStatus = STEPPER_BUTTON_STATUS.FINALIZE_AND_LAUNCH;
        break;
      case 'goToMapQuotas':
        this._router.navigateByUrl(
          `/quota-mapper/${this.getSurveyId()}`,
          this._prepareNavigationExtras()
        );
        this.buttonStatus = STEPPER_BUTTON_STATUS.MAP_QUOTAS;
        break;
      case 'LiveEdit':
        let surveyId = this.getSurveyId();
        if (
          this.surveyPayload?.channel_type == TRAFFIC_CHANNEL.SPECIFIC_AUDIENCE
        ) {
          surveyId = this._getSurveyRedirectIdForSpaSurveys(
            this.surveyPayload
          )!;
        }
        this._cleanMcSvpSurveyData();
        if (!this.isInsight) {
          this._router.navigateByUrl(`/dashboard/${surveyId}`);
        }
        break;
      case 'OpenModal':
        this._openSurveyLaunchModal();
        if (surveyResponse?.price_override) {
          this.surveyPayload.price_override = Number(
            surveyResponse.price_override
          );
        }
        break;
      default:
        break;
    }
    this._setUpdateSurveyDetails(surveyResponse as BuyerSurveyResponse, action);
  }

  private _getSurveyRedirectIdForSpaSurveys(survey: SurveyPayload) {
    return (
      survey.ps_mc_project_id ??
      survey.ps_qbp_project_id ??
      survey.ps_project_id
    );
  }

  private _cleanMcSvpSurveyData() {
    this._multiCountryDraftService.resetMultiCountryData();
  }

  private _validateMcQbSurvey() {
    if (this._isMcSvpSurvey()) {
      this._toastr.info(
        notifyMessage.infoMessage.MC_SINGLE_VIEW.CLONE_MC_SVP_NOT_AVAILABLE
      );
      return true;
    }
    return false;
  }

  private _setUpdateSurveyDetails(
    surveyResponse: BuyerSurveyResponse,
    action: null | string = null
  ) {
    this._marketplaceService.setUpdateSurveyDetails(surveyResponse, action);
  }

  private _updateHeaderInfo(surveyResponse?: SurveyNoTargetingResponse) {
    if (surveyResponse) {
      const surveyR = surveyResponse as BuyerSurveyResponse;
      let totalClicks = 0;
      if (surveyR.click_balancing) {
        surveyR.click_balancing = surveyR.click_balancing ? 1 : 0;
        totalClicks = Math.round((surveyR.goal * 100) / surveyR.expected_ir);
      }
      if (!surveyR.default_pure_price_override) {
        this._surveyMetaDataService.setPurePriceOverride(
          new PurePriceOverride(false, 0)
        );
      }
      this._surveyPayloadService.addTotalClickInHeaderBar(surveyR, totalClicks);
    }
  }

  private _mapPayload() {
    if (this.currentPage !== SURVEY_PAGES.LAUNCH) {
      this.surveyPayload.pricing_multiplier = this.pricingMultiplier;
    }
    if (this.surveyPayload.quotas?.length) {
      this.surveyPayload.quotas = this.surveyPayload.quotas.map((quota) => {
        delete quota.suppliers;
        return quota;
      });
    }
    if (this.surveyPayload.channel_type === TRAFFIC_CHANNEL.SPECIFIC_AUDIENCE) {
      if (this.surveyPayload?.bpm?.nm && this.surveyPayload?.bpm?.eml) {
        this.surveyPayload.bpm.name = this.surveyPayload.bpm.nm;
        this.surveyPayload.bpm.email = this.surveyPayload.bpm.eml;
      }
      this.surveyPayload.ps_survey_status = SURVEY_STATUS.LIVE;
    }
    if (this.surveyPayload.price_override) {
      this.cpi.forEach((supplier) => {
        supplier.cpi = Number(this.surveyPayload.price_override);
      });

      this.surveyPayload.offer_price = this.surveyPayload.price_override;
      delete this.surveyPayload.price_override;
    }
    if (
      (!this.templateResponse || !this.templateResponse.template) &&
      this.surveyPayload.template_id
    ) {
      delete this.surveyPayload.template_id;
    }

    if (
      this.surveyPayload?.aida?.enabled &&
      this.surveyStatus === this.surveyStatusObj.DRAFT
    ) {
      this._transformAidaPayload();
    }

    if (
      this.surveyPayload?.qbp?.enable &&
      Common.isMultiNestedQuotasAvailable(this.surveyPayload.quotas ?? []) &&
      this.surveyStatus === SURVEY_STATUS.DRAFT
    ) {
      this.surveyPayload.qbp = DEFAULT.QBP;
    }

    this.updateFeotCustomPacingFields();

    if (this.currentPage === SURVEY_PAGES.LAUNCH) {
      this._assignParentToMcTraditionalSurvey();
    }
    return this._surveyPayloadService.removeEmpty(
      {
        ...this.surveyPayload,
      },
      Constants.BLANK_PAYLOAD_EXCEPTION
    );
  }

  private _transformAidaPayload() {
    if (
      !this.surveyPayload.quotas?.length &&
      !this.surveyPayload.aida.selected_quota_ids?.length
    ) {
      return;
    }

    const aida = new AidaSurveyPayloadTransformer(
      {
        ...this.surveyPayload,
        ps_survey_status: this.surveyStatus,
      },
      this._aidaPayloadService.getMappedDBQualificationCodes()
    ).transform();
    this.surveyPayload.aida = { ...aida };
  }

  public updateFeotCustomPacingFields() {
    if (!this.surveyPayload.is_feot_enabled) {
      return;
    }
    if (!this.surveyPayload.feot?.is_custom) {
      return;
    }
    this.surveyPayload.feot.pacing =
      this.surveyPayload.feot.pacing ||
      SURVEY_LAUNCH_CONSTANTS.FEOT.CUSTOM_PACING.DEFAULT_PACING;
    this.surveyPayload.feot.interval =
      this.surveyPayload.feot.interval ||
      SURVEY_LAUNCH_CONSTANTS.FEOT.CUSTOM_PACING.DEFAULT_PACING_INTERVAL;
  }

  private _isValidPayload() {
    const validPSID = this._validatePSIDList();
    if (!validPSID) {
      return false;
    }
    if (
      this.surveyPayload?.schedule_launch?.enable &&
      (!this.surveyPayload.schedule_launch.info ||
        !this.surveyPayload.schedule_launch.info.date ||
        !this.surveyPayload.schedule_launch.info.hours ||
        !this.surveyPayload.schedule_launch.info.timezone)
    ) {
      this._toastr.error(
        notifyMessage.errorMessage.SURVEY_LAUNCH.EMPTY_SCHEDULE_LAUNCH
      );
      return false;
    }
    return true;
  }
  private _validatePSIDList() {
    if (!this.surveyPayload.incl_excl) {
      return true;
    }
    const surveyGrouping = this._filterSurveyGrouping();
    const refList = this.surveyPayload.ref_list || surveyGrouping;
    if (!refList?.length) {
      this._toastr.error(
        notifyMessage.errorMessage.SURVEY_LAUNCH.RESPONDENT_LIST_MISSING
      );
      return false;
    }
    return true;
  }
  private _isLiveUrlExist() {
    if (this.surveyPayload?.live_url) {
      return true;
    }
    return false;
  }

  private _missingRequiredBpm() {
    if (this._isBpmRequired && !this.surveyPayload?.bpm?.id) {
      return true;
    }
    return false;
  }

  private _modifyStringForBuyerRole(string: string) {
    if (this._isOperator) {
      return string.replace(/Project Manager/g, 'Buyer Project Manager');
    }

    return string;
  }

  private _quotaMapperValidator(quotas: QuotaPayload[]) {
    let qlcValidationFlag = false;
    quotas.some((quota: QuotaPayload) => {
      if (!quota.buyer_quota_id) {
        qlcValidationFlag = true;
        return true;
      }
      return false;
    });
    if (!qlcValidationFlag) {
      let groupdQuotasforUniquness = quotas.reduce(function (a: any, e: any) {
        let estKey = e['buyer_quota_id'];

        (a[estKey] ? a[estKey] : (a[estKey] = null || [])).push(e);
        return a;
      }, {});

      let unique = '';
      Object.keys(groupdQuotasforUniquness).forEach((groupedQuotaKey, i) => {
        if (groupdQuotasforUniquness[groupedQuotaKey].length > 1) {
          unique = QUOTA_LEVEL_TRACKING.UNIQUE_FAIL;
          return true;
        } else {
          return false;
        }
      });
      if (unique) {
        const modalRef = this._modal.open(QuotaLevelTrackingModalComponent, {
          data: {
            surveyId: this.surveyId,
            respMessage: unique,
            duplicatedBuyerQuotaIds: this.duplicatedBuyerQuotaIds,
          },
        });
        return true;
      }
      let groupdQuotasforCharCount = [];
      quotas.forEach((quota: QuotaPayload) => {
        if (quota.buyer_quota_id && quota.buyer_quota_id.length > 15) {
          groupdQuotasforCharCount.push(quota.buyer_quota_id);
        }
      });
      if (groupdQuotasforCharCount.length >= 1) {
        const modalRef = this._modal.open(QuotaLevelTrackingModalComponent, {
          data: {
            surveyId: this.surveyId,
            respMessage: QUOTA_LEVEL_TRACKING.CHAR_COUNT_VALIDATION_FAIL,
            charCountExceedQuotas: this.charCountExceedQuotas,
          },
        });
        return true;
      }
    }
    if (qlcValidationFlag) {
      const modalRef = this._modal.open(QuotaLevelTrackingModalComponent, {
        data: {
          surveyId: this.surveyId,
          respMessage: QUOTA_LEVEL_TRACKING.INPUT_VALIDATION_FAIL,
        },
      });
      return true;
    } else {
      this.updateSurvey('OpenModal');
      return true;
    }
  }

  private _onMcSpvSelection() {
    if (!this._isMcSvpSurvey()) {
      return;
    }
    this.updateSuppliers(false);
  }

  updateSuppliers(redirect: boolean = true) {
    if (
      this._aidaSurveyErrorDetails &&
      this._aidaSurveyErrorDetails.showError
    ) {
      this._toastr.error(this._aidaSurveyErrorDetails.message);
      return;
    }

    const surveyId = this._isMcSvpSurvey()
      ? this._previouslySelectedMcSpvChildId
      : this.surveyId;
    if (!surveyId) {
      this._previouslySelectedMcSpvChildId =
        this.selectedMcSpvChild?.ps_survey_id;
      return;
    }

    if (!this.cpiOverrideConfirmationState) {
      this._toastr.error(notifyMessage.CPI_CONFIRMATION_ERROR);
      return;
    }

    if (
      this.supplierPayload.operatorOverrideMdl &&
      this.supplierPayload.mcQbOverrideMdl &&
      this._isMcSvpSurvey()
    ) {
      this._updateMcSvpChildSurveysOfferPrice();
    }
    this._editSupplierModalService
      .updateSuppliers(surveyId, this.supplierPayload)
      .then((isUpdated) => {
        if (isUpdated) {
          if (!redirect && this.selectedMcSpvChild) {
            this._previouslySelectedMcSpvChildId =
              this.selectedMcSpvChild.ps_survey_id;
            this._eventEmitterService.sendMcSvpSuppliersDataSaved();
          }
          if (redirect) {
            this._actionAfterUpdate('LiveEdit');
          }
        }
      });
  }

  editSurvey() {
    if (this.currentPage === SURVEY_PAGES.SUPPLIER) {
      this.updateSuppliers();
    } else if (
      this.currentPage === SURVEY_PAGES.AUDIENCE &&
      !this.surveyPayload.channel_type
    ) {
      // open pro-rata modal if quota supplier CT is changed
      let supplierCurrentTargetChanged = false;
      this.surveyPayload.quotas?.forEach((quota) => {
        const currentTargetChangedIndex = quota.suppliers
          ? quota.suppliers.findIndex(
              (supplier: { current_target: number; complete_needed: any }) => {
                return (
                  supplier.current_target !== quota.current_target &&
                  supplier.complete_needed
                );
              }
            )
          : -1;
        if (currentTargetChangedIndex > -1) {
          supplierCurrentTargetChanged = true;
        }
      });
      if (this.surveyData?.activeTrafficChannel) {
        this._openActiveTCWarningModal(supplierCurrentTargetChanged);
      } else {
        this._openEditModal(supplierCurrentTargetChanged);
      }
    } else if (this.currentPage === SURVEY_PAGES.LAUNCH) {
      if (
        this.surveyPayload.unique_links?.enable &&
        (!this.surveyPayload.unique_links.items ||
          !this.surveyPayload.unique_links.items.length)
      ) {
        return this._toastr.error(
          notifyMessage.errorMessage.SURVEY_LAUNCH.UNIQUE_BUYER_LINKS_NOT_UPLOAD
        );
      }
      if (!this.surveyPayload.unique_links?.enable && !this._isLiveUrlExist()) {
        return this._toastr.error(
          notifyMessage.errorMessage.SURVEY_LAUNCH.EMPTY_LIVE_URL
        );
      }
      if (this._missingRequiredBpm()) {
        return this._toastr.error(
          this._modifyStringForBuyerRole(
            notifyMessage.errorMessage.SURVEY_LAUNCH.BPM_REQUIRED_ON_UPDATE
          )
        );
      }

      this.updateSurvey('LiveEdit');
    } else {
      this.updateSurvey('LiveEdit');
    }
  }

  private _openEditModal(supplierCurrentTargetChanged: boolean) {
    if (supplierCurrentTargetChanged) {
      this._openProRataModal();
    } else {
      this._openEditSurveyModal();
    }
  }

  private _openActiveTCWarningModal(supplierCurrentTargetChanged: boolean) {
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: [
          'Making these changes will close your live Traffic Channels. You will need to create new Traffic Channels if you make these changes.',
          'Would you like to continue?',
        ],
        cancelButtonLabel: 'NO',
        confirmButtonLabel: 'YES',
      },
      maxWidth: '450px',
    });
    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'ok') {
        this._openEditModal(supplierCurrentTargetChanged);
      } else {
        return;
      }
    });
  }

  private _openProRataModal() {
    this.surveyPayload.update_supplier = true;
    const modalRef = this._modal.open(SupplierCtUpdateWarningModalComponent);
    modalRef.onClose$.subscribe((msg) => {
      if (!msg) {
        return;
      }
      if (msg === 'continue-as-is') {
        this.surveyPayload.update_supplier = false;
      }
      this.updateSurvey('LiveEdit');
    });
  }

  private _openEditSurveyModal() {
    const modalRef = this._modal.open(EditSurveyConfirmationModalComponent);
    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'continue') {
        this.updateSurvey('LiveEdit');
      } else {
        return;
      }
    });
  }

  private _redirectToNextSurvey() {
    if (
      this.surveyData?.surveyId == this.surveyId &&
      this.surveyData?.mcUniqueId
    ) {
      this.multiCountrySurveyData.map((mcSurvey) => {
        if (mcSurvey.survey_id == this.surveyId) {
          mcSurvey.ps_survey_status = SURVEY_STATUS.LIVE;
        }
      });
      this._multiCountryDraftService.setMultiCountrySurveyData(
        this.multiCountrySurveyData
      );

      let draftSurvey = this.multiCountrySurveyData.find(
        (mcSurvey) => mcSurvey.ps_survey_status == SURVEY_STATUS.DRAFT
      );
      if (draftSurvey) {
        this._router.navigateByUrl(`/surveys/${draftSurvey.survey_id}/edit`);
      } else {
        this.goToDashboard();
      }
    } else {
      this.goToDashboard();
    }
  }

  private goToDashboard() {
    this._cleanMcSvpSurveyData();
    if (!this.isInsight) {
      this._router.navigateByUrl('/dashboard');
    }
  }

  private _getSurveyData() {
    this._subscriptions.add(
      this._surveyMetaDataService.getSurveyData().subscribe((surveyData) => {
        this.surveyData = surveyData;
      })
    );
  }

  private _getMultiCountryData() {
    this._subscriptions.add(
      this._multiCountryDraftService
        .getMultiCountrySurveyData()
        .subscribe((data) => {
          this.multiCountrySurveyData = data;
        })
    );
  }

  private _cloneSurvey(id: number) {
    if (this._validateMcQbSurvey()) {
      return;
    }
    this._buyerApiService
      .cloneSurvey(id, this.isServiceBuyerLoggedIn)
      .subscribe(
        (response) => {
          const clonedSurveyId = response.ps_survey_id;
          this._router.navigateByUrl(`/surveys/${clonedSurveyId}/edit`);
        },
        (error) => {
          this._toastr.error(error.error.ps_api_response_message);
        }
      );
  }

  private _launchscheduleLaunchSurvey() {
    if (
      this.surveyPayload.unique_links?.enable &&
      (!this.surveyPayload.unique_links.items ||
        !this.surveyPayload.unique_links.items.length)
    ) {
      return this._toastr.error(
        notifyMessage.errorMessage.SURVEY_LAUNCH.UNIQUE_BUYER_LINKS_NOT_UPLOAD
      );
    }
    if (!this.surveyPayload.unique_links?.enable && !this._isLiveUrlExist()) {
      return this._toastr.error(
        notifyMessage.errorMessage.SURVEY_LAUNCH.EMPTY_LIVE_URL
      );
    }
    if (this._missingRequiredBpm()) {
      return this._toastr.error(
        this._modifyStringForBuyerRole(
          notifyMessage.errorMessage.SURVEY_LAUNCH.BPM_REQUIRED_ON_LAUNCH
        )
      );
    }
    this.updateSurvey('Launch');
  }

  private _validatePayloadForTemplate() {
    try {
      this._validatePayload();
      return true;
    } catch (e: any) {
      this._toastr.error(e);
      return false;
    }
  }

  private _validateTemplateScenarios(): boolean {
    return [
      () => this._validateIfTemplateCanBeApplied(),
      () => this._validatePayloadForTemplate(),
    ].every((check) => check());
  }

  public openNewTemplateModal() {
    const isValid = this._validateTemplateScenarios();
    if (!isValid) {
      return;
    }
    const modal = this._modal.open(TemplateModalComponent, {
      width: '50%',
    });

    modal.onClose$
      .pipe(
        filter((template: SelectedTemplate) => !!template),
        map(
          (
            selectedTemplate
          ): [number | undefined, TemplateRequest, SelectedTemplate] => [
            selectedTemplate.template.id,
            this._mapTemplatePayload(selectedTemplate),
            selectedTemplate,
          ]
        ),
        switchMap(([id, payload, selectedTemplate]) =>
          this._saveOrUpdateTemplate(payload, id, selectedTemplate)
        )
      )
      .subscribe(
        () => {
          this._toastr.success(
            notifyMessage.successMessage.TEMPLATES.TEMPLATE_SAVED
          );
        },
        (error) => this._toastr.error(error)
      );
  }

  private _saveOrUpdateTemplate(
    payload: TemplateRequest,
    id?: number,
    optedTemplate?: SelectedTemplate
  ) {
    return iif(
      () => !id,
      defer(() => this._templateService.saveTemplate(payload)),
      defer(() => {
        // In update template scenario, if the loaded template is same as template selected from the modal, we show confirmation modal, otherwise we skip modal
        const skipModal = Number(id) !== this.templateResponse?.template?.id;
        this.checkAndUpdateTemplate(skipModal, optedTemplate);
        return new Promise(() => 'update'); // Sending promise to avoid being subscribed to, since 'checkAndUpdateTemplate' has its own toastr message on updation
      })
    );
  }

  public checkAndUpdateTemplate(
    skipModal: boolean = false,
    optedTemplate?: SelectedTemplate
  ) {
    let isValid = this._validateTemplateScenarios();
    if (!isValid) {
      return;
    }
    let templateToUpdate: SelectedTemplate;
    if (optedTemplate) {
      templateToUpdate = optedTemplate;
    } else if (
      this.templateResponse &&
      Object.keys(this.templateResponse).length
    ) {
      templateToUpdate = this.templateResponse;
    } else {
      templateToUpdate = {} as TemplateResponse;
    }
    isValid = this._validateTemplateOwnerShip(templateToUpdate);
    if (!isValid) {
      return;
    }
    if (skipModal) {
      this._updateTemplate(templateToUpdate, false);
      return;
    }
    const modalRef = this._modal.open(ConfirmationModalComponent, {
      data: {
        text: LayoutConstants.CONFIRM_MODAL.TEMPLATES.TEXT,
        cancelButtonLabel:
          LayoutConstants.CONFIRM_MODAL.TEMPLATES.CANCEL_BUTTON_TEXT,
        confirmButtonLabel:
          LayoutConstants.CONFIRM_MODAL.TEMPLATES.CONFIRM_BUTTON_TEXT,
      },
    });
    modalRef.onClose$.subscribe((msg) => {
      if (msg === 'ok') {
        this._updateTemplate(templateToUpdate, true);
      }
    });
  }

  private _validateTemplateOwnerShip(selectedTemplate: SelectedTemplate) {
    const cmpId = this.isServiceBuyerLoggedIn
      ? this._auth.serviceBuyer?.id
      : this._auth.user?.cmp;
    if (
      selectedTemplate &&
      selectedTemplate.template &&
      selectedTemplate.template.owner &&
      selectedTemplate.template.owner !== cmpId
    ) {
      this._toastr.warning(
        notifyMessage.warningMessage.TEMPLATES.TEMPLATE_MODIFICATION_NOT_ALLOWED
      );
      return false;
    }
    return this._validateWASPOwnerShip(selectedTemplate);
  }

  private _validateWASPOwnerShip(selectedTemplate: SelectedTemplate) {
    if (
      this.isTargetMarginEnabledForOperatorBuyer &&
      !selectedTemplate?.template.is_service_buyer
    ) {
      this._toastr.warning(
        notifyMessage.warningMessage.TEMPLATES.TEMPLATE_MODIFICATION_NOT_ALLOWED
      );
      return false;
    }
    return true;
  }

  private _updateTemplate(
    templateToUpdate: SelectedTemplate,
    isLoadedTemplate: boolean
  ) {
    // Update the selected/already populated template
    if (
      templateToUpdate &&
      Object.keys(templateToUpdate).length &&
      templateToUpdate.template.id
    ) {
      const payload = this._mapTemplatePayload(templateToUpdate);
      this._templateService
        .updateTemplate(String(templateToUpdate.template.id), payload)
        .subscribe(
          (resp) => {
            if (isLoadedTemplate) {
              this.updatedTemplate.emit(resp);
            }
            this._toastr.success(
              notifyMessage.successMessage.TEMPLATES.TEMPLATE_UPDATED
            );
          },
          ({ error: { ps_api_response_message = '' } = {} }) => {
            this._toastr.error(ps_api_response_message);
          }
        );
    }
  }

  private _mapTemplatePayload({
    folder,
    is_suppliers_list,
    is_mc_template,
    template,
  }: SelectedTemplate): TemplateRequest {
    const survey = this._mapPayload();
    const blend = this.supplierPayload?.blend_id;
    let waspPayload = {};
    if (this.isTargetMarginEnabledForOperatorBuyer) {
      const { margin_override: { margin = 50 } = {}, offer_price = 0 } = survey;
      waspPayload = { margin, offer_price };
    }

    return {
      folder: folder.id,
      is_suppliers_list,
      suppliers_data: {
        blend_id: blend ? blend : '',
        suppliers_allocation: is_suppliers_list
          ? this.supplierPayload?.suppliers
          : [],
      },
      survey_id: this.surveyId,
      is_mc_template,
      template: {
        name: template.name,
        survey_title: survey.survey_title,
        click_balancing: survey.click_balancing,
        completes_required: survey.completes_required,
        estimated_clicks: survey.estimated_clicks,
        expected_ir: survey.expected_ir,
        expected_loi: survey.expected_loi,
        field_time: survey.field_time,
        is_feot_enabled: survey.is_feot_enabled,
        qualifications: survey.qualifications,
        quotas: survey.quotas,
        schedule_launch: survey.schedule_launch,
        soft_launch: survey.soft_launch,
        survey_category_code: Constants.TEMPLATE_DEFAULT_CATEGORY,
        survey_localization: survey.survey_localization,
        supplier_flexibility: this.supplierPayload?.suppliers?.length
          ? this.supplierPayload?.suppliers[0].flexValue
          : 100,
        is_service_buyer: this.isServiceBuyerLoggedIn,
        ...(is_mc_template && this._getMcUniqueIdPayload()),
        ...waspPayload,
      },
    };
  }

  private _getMcUniqueIdPayload() {
    return { mc_unique_id: this.surveyData?.mcUniqueId };
  }

  private _validateFeotIntervalTime() {
    if (
      this.surveyPayload.is_feot_enabled &&
      this.surveyPayload.feot?.is_custom &&
      this.surveyPayload.feot?.interval &&
      this.surveyPayload.feot?.interval > this.feotFields.feotRemainingTime
    ) {
      throw new Error(
        notifyMessage.errorMessage.SURVEY_LAUNCH.FEOT_INTERVAL_BIGGER_THAN_FIELD_TIME
      );
    }
  }

  private _getFeotRemainingTime() {
    switch (this.surveyPayload.feot?.pacing) {
      case 'hours':
        return Math.ceil(this.surveyPayload.field_time * 24);
      case 'weeks':
        return Math.ceil(this.feotFields.feotRemainingTime / 7);
      default:
        return Math.ceil(this.feotFields.feotRemainingTime);
    }
  }

  private _filterSurveyGrouping() {
    if (this.surveyPayload.survey_grouping?.length) {
      return this.surveyPayload.survey_grouping.filter(
        (surveyId) => surveyId !== this.surveyId
      );
    }
    return [];
  }

  private _validateIfTemplateCanBeApplied() {
    if (!this.surveyPayload?.mc?.SVP) {
      return true;
    }
    this._toastr.info(
      notifyMessage.infoMessage.TEMPLATE.NOT_AVAILABLE_FOR.MC_SINGLE_VIEW
    );
    return false;
  }

  private _updateMcSvpChildSurveysOfferPrice() {
    this._multiCountryService
      .updateSurvey(
        this._mcSvpPayloadComposer.payload()!,
        this._mcSvpPayloadComposer.surveyId()
      )
      .toPromise();
  }

  private _validateSurveyLinksAndUrl() {
    const mcqbSurveys = this._surveyPayload()?.surveys;
    return mcqbSurveys.every((survey: McSpvSurveyRequest) => {
      // Check if unique_links.enable is true
      if (survey.unique_links && survey.unique_links.enable === true) {
        if (
          !Array.isArray(survey.unique_links.items) ||
          survey.unique_links.items.length === 0
        ) {
          // If items is empty, return false and show the "unique links" error message
          this._toastr.error(
            notifyMessage.errorMessage.SURVEY_LAUNCH
              .UNIQUE_BUYER_LINKS_NOT_UPLOAD
          );
          return false;
        }
      }
      // Check if unique_links.enable is false
      else if (!survey.unique_links?.enable && !survey.live_url) {
        this._toastr.error(
          notifyMessage.errorMessage.SURVEY_LAUNCH.EMPTY_LIVE_URL
        );
        return false;
      }
      return true; // Return true if no validation fails
    });
  }

  private _handleAidaSupplierErrorDetails() {
    const errorSubscription = this._aidaPayloadService
      .getAidaSurveyErrorDetails()
      .pipe(filter(() => this.surveyStatus !== SurveyStatus.Draft))
      .subscribe((data) => {
        this._aidaSurveyErrorDetails = data;
      });
    this._subscriptions.add(errorSubscription);
  }

  private _assignParentToMcTraditionalSurvey() {
    if (this.surveyPayload?.mc_unique_id && !this.surveyPayload?.mc?.SVP) {
      const hasMcParent = this.multiCountrySurveyData.some(
        (mcSurvey) => !!mcSurvey.mc_parent
      );

      if (!hasMcParent) {
        this.surveyPayload.mc = {
          ...(this.surveyPayload.mc || {}),
          parent: true,
        };
      }
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
